import axios from "vue-app/utils/axios";
import { SORT_DIRECTION } from "vue-app/types/enums";
import {
  DTOProductData,
  DTOProductsAvailabilityStatusDataScienceResponse,
  DTOTrackerProductBestsellersResponse,
  DTOTrackerBestsellersSettingsLockStatusResponse,
  DTOTrackerBestsellersSettingsResponse,
  DTOTrackerCategoriesV2Response,
  DTOTrackerCategoryProductsTableV3Response,
  DTOTrackerProductsStatusCountResponse,
  DTOTrackerProductsStatusResponse,
  DTOTrackerProductStatisticResponse,
} from "./dto";
import { PRODUCT_RECOMMENDATION_STATUS_NUMBER } from "vue-app/components/ProductReport/ProductReport.enum";

const ROUTE = "/category";

export interface GetTrackerCategoryProductsTableV2QueryParams {
  trackerProductCategoryId: number | null;
  productIds?: number[];
  offset: number;
  limit: number;
  sortBy?: TRACKER_CATEGORY_PRODUCT_TABLE_SORT_KEY | null;
  sortOrder?: SORT_DIRECTION;
  minPrice?: number | null;
  maxPrice?: number | null;
  minConversion?: number | null;
  maxConversion?: number | null;
}

export enum TRACKER_CATEGORY_PRODUCT_TABLE_SORT_KEY {
  PRICE = "price",
  CONVERSION = "conversion",
  TOTAL_ADDED_TO_CART = "total_added_to_cart",
  TOTAL_ORDERED = "total_ordered_by_customer",
  TOTAL_VIEWS = "total_views",
  TOTAL_REVENUE = "total_revenue",
  TOTAL_ORDERED_COPIES = "total_ordered_copies",
}

export interface GetRecommendationTrackerProductStatusPayload {
  trackerProductIds?: string[] | number[];
  status?: PRODUCT_RECOMMENDATION_STATUS_NUMBER;
}

export interface SetRecommendationTrackerProductStatusPayload {
  trackerProductIds: string[];
  status: PRODUCT_RECOMMENDATION_STATUS_NUMBER;
}

export const getTrackerCategoriesV2 = async (): Promise<DTOTrackerCategoriesV2Response> => {
  const url = `${ROUTE}/v3/tracker-categories`;
  const { data } = await axios.get<{ data: DTOTrackerCategoriesV2Response }>(url);

  return data.data;
};

export const getTrackerCategoryProductsTableV2 = async ({
  trackerProductCategoryId,
  productIds,
  limit,
  offset,
  sortBy,
  sortOrder,
  minPrice,
  maxPrice,
  minConversion,
  maxConversion,
}: GetTrackerCategoryProductsTableV2QueryParams): Promise<DTOTrackerCategoryProductsTableV3Response> => {
  const url = `${ROUTE}/v3/tracker-category-products-table`;
  const body: Record<string, unknown> = {
    limit,
    offset,
  };

  if (trackerProductCategoryId) {
    body.tracker_product_category_id = trackerProductCategoryId;
  }

  if (sortBy) {
    body.sort_by = sortBy;
  }

  if (sortOrder) {
    body.sort_order = sortOrder;
  }

  if (productIds && productIds.length > 0) {
    body.tracker_product_ids = productIds;
  }

  if (minPrice !== null) {
    body.min_price = minPrice;
  }

  if (maxPrice !== null && maxPrice !== 0) {
    body.max_price = maxPrice;
  }

  if (minConversion !== null) {
    body.min_conversion = minConversion;
  }

  if (maxConversion !== null && maxConversion !== 0) {
    body.max_conversion = maxConversion;
  }

  const { data } = await axios.post<DTOTrackerCategoryProductsTableV3Response>(url, body);

  return data;
};

export const getTrackerProductInfoV2 = async (trackerProductId: string): Promise<DTOProductData> => {
  const queryParams = new URLSearchParams({
    tracker_product_id: trackerProductId,
  });

  const url = `${ROUTE}/v3/tracker-product-info?${queryParams.toString()}`;
  const { data } = await axios.get<DTOProductData>(url);

  return data;
};

export const getTrackerProductStatisticV2 = async (
  appIdProductId: string,
  dateFrom: number,
  dateTo: number
): Promise<DTOTrackerProductStatisticResponse> => {
  const queryParams = new URLSearchParams({
    app_id_product_id: appIdProductId,
    date_from: dateFrom.toString(),
    date_to: dateTo.toString(),
  });

  const url = `${ROUTE}/v3/tracker-product-statistic?${queryParams.toString()}`;
  const { data } = await axios.get<DTOTrackerProductStatisticResponse>(url);

  return data;
};

export const getTrackerProductAvailabilityStatus = async (
  trackerProductId: number[]
): Promise<DTOProductsAvailabilityStatusDataScienceResponse> => {
  if (trackerProductId.length === 0) {
    return { data: [] };
  }

  const idsAsString = trackerProductId.join(",");

  const url = `${ROUTE}/v3/tracker-product-availability-status?tracker_product_ids=${idsAsString}`;

  const { data } = await axios.get<DTOProductsAvailabilityStatusDataScienceResponse>(url);
  return data;
};

export const getTrackerProductsBestsellers = async () => {
  const url = `${ROUTE}/v3/bestsellers`;
  const { data } = await axios.get<{ data: DTOTrackerProductBestsellersResponse }>(url);

  return data.data;
};

export const getBestsellersSettings = async (): Promise<DTOTrackerBestsellersSettingsResponse> => {
  const url = `${ROUTE}/v3/bestsellers/settings`;
  const { data } = await axios.get<DTOTrackerBestsellersSettingsResponse>(url);

  return data;
};

export const setBestsellersSettings = async (minNumberOfOrders: number, minProductPrice: number): Promise<void> => {
  const url = `${ROUTE}/v3/bestsellers/settings`;
  await axios.post<void>(url, {
    min_number_of_orders: minNumberOfOrders,
    min_product_price: minProductPrice,
  });
};

export const refreshBestsellersSettings = async (): Promise<void> => {
  const url = `${ROUTE}/v3/bestsellers/refresh`;
  await axios.post<void>(url);
};

export const getRecommendationTrackerProductStatus = async (
  params: GetRecommendationTrackerProductStatusPayload
): Promise<DTOTrackerProductsStatusResponse> => {
  const queryParams = new URLSearchParams();

  if (params.trackerProductIds) {
    const ids = params.trackerProductIds.join(",");
    queryParams.append("tracker_product_ids", ids);
  }

  if (params.status) {
    queryParams.append("status", params.status.toString());
  }

  const baseURL = `${ROUTE}/v3/recommendations/tracker-products/status`;
  const url = queryParams.size > 0 ? `${baseURL}?${queryParams.toString()}` : baseURL;

  const { data } = await axios.get<DTOTrackerProductsStatusResponse>(url);
  return data;
};

export const setRecommendationTrackerProductStatus = async (params: SetRecommendationTrackerProductStatusPayload) => {
  const url = `${ROUTE}/v3/recommendations/tracker-products/status`;

  await axios.post<void>(url, { tracker_product_ids: params.trackerProductIds, status: params.status });
};

export const getRecommendationTrackerProductStatusCount = async (): Promise<DTOTrackerProductsStatusCountResponse> => {
  const url = `${ROUTE}/v3/recommendations/tracker-products/status/count`;
  const { data } = await axios.get<DTOTrackerProductsStatusCountResponse>(url);

  return data;
};

export const getBestsellersSettingsLockStatus = async (): Promise<
  Required<DTOTrackerBestsellersSettingsLockStatusResponse>
> => {
  const url = `${ROUTE}/v3/bestsellers/settings/lock-status`;
  const { data } = await axios.get<DTOTrackerBestsellersSettingsLockStatusResponse>(url);

  return data;
};
